import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import { QuoteOrderModelType } from '@teinor/erp/types/company/order/quote/quoteOrder'
import { Fragment, useCallback, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import ModalQuoteOrderDuplicate from '../../../../../app/erp/screens/Logistic/QuoteParent/QuoteOrders/SingleQuoteOrder/ModalQuoteOrderDuplicate'
import { WhiteBox } from '../../../../../baseComponents/AppLayout/WhiteBox'
import { ButtonPrimary } from '../../../../../baseComponents/Button'
import LoadingSpinner from '../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import { ModalOpenedTy } from '../../../../../baseComponents/Modal/types'
import TwinIcon, { TwinIconToolTip } from '../../../../../baseComponents/TwinIcon'
import TwinTrans from '../../../../../baseComponents/TwinTrans'
import { RadioWithLabel } from '../../../../../forms/RadioSelector'
import twinFetchPost from '../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import { getBasePath } from '../../../../../utils/globals/link'
import useEditDeleteModal from '../../../../../utils/hooks/useEditDeleteModal'
import useOnClickOutside from '../../../../../utils/hooks/useOnClickOut'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { changeErrorMessage } from '../../../../../utils/reducers/reduxDispatch'
import PermissionChecker from '../../../../../baseComponents/PermissionChecker'
import { getUserPermissions } from '../../../../../utils/reducers/getters'
import { faCheckCircle, faCloudArrowDown, faCopy, faFilePdf, faFilePlus, faXmarkCircle, faArrowRotateRight, faComments, faShare, faShareNodes, faArrowsRotate } from '@fortawesome/pro-light-svg-icons'
import useIsLoading from '../../../../../utils/hooks/useIsLoading'
import { OrderData } from '../../OrderLayout/types'
import ModalCancelQuote from './ModalDeleteQuote'
import OrderSelectorPdfTemplateConnect from '../../OrderLayout/OrderButtons'
import ModalQuoteComments from './ModalQuoteComments'
import ModalShareAndUnshareQuote from './ModalShareQuote'
import ModalReopenQuote from './ModalReopenQuote'
import ModalNewVersionQuote from './ModalNewVersionQuote'
import ModalFinalizeQuote from './ModalFinalizeQuote'
import ModalRecalculateQuote from './ModalRecalculateQuote'

interface QuoteOrderVersionsSelectorProps extends QuoteOrderVersionsSelectorLogicProps {}

export const QuoteOrderVersionsSelector: React.FC<QuoteOrderVersionsSelectorProps> = ({ versions, currentValue, url }) => {
    const {t} = useTwinTranslation()
    const { previousQuoteOrders, onChangePreviousQuoteOrder, onChangeQuoteVersionChecked, ref, openedModal, changeOpened } = useQuoteOrderVersionsSelectorLogic({ url, versions, currentValue })
    const renderThis: JSX.Element[] = []
    const items = previousQuoteOrders?.previousQuoteOrders
    let currentVN = 1
    let nextV : null | number = null
    let prevV : null | number = null
    if (items) {
        for (const versionKey in items) {
            const version = items[versionKey]
            if (version.id === currentValue) {
                currentVN = version.version || 1
                prevV = items?.[parseInt(versionKey)+1]?.id || null
                nextV = items?.[parseInt(versionKey)-1]?.id || null
            }
            renderThis.push(<RadioWithLabel label={(String(version.version) || '') + (version.id === currentValue ? ( ' ('+t('actual', 'actual')+')') : '') } id={'selectedVersion' + version.id} className='mb-15' name='selectedFilter' checked={previousQuoteOrders?.selectedQuoteVersion === version.id} key={version.id} onChange={() => onChangeQuoteVersionChecked(version.id || 0)} />)
        }
    } else {
        renderThis.push(<LoadingSpinner className='h-30 mb-15' />)
    }
    if (currentVN === 1 && !nextV) {
        return null
    }
    return (
        <div className='relative ml-auto mr-5' ref={ref}>
            <div className='text-green-21 flex items-center'>
                <div className='min-w-20 mt-2'>
                    {prevV ? <TwinIcon icon={faChevronLeft} className='w-20 cursor-pointer' onClick={() => onChangePreviousQuoteOrder(prevV || 0)} /> : null}
                </div>
                <span className='regular16 min-w-20 text-center cursor-pointer' onClick={changeOpened} >v{currentVN}</span>
                {nextV ? 
                    <div className='min-w-20 mt-2'>
                        <TwinIcon icon={faChevronRight} className='w-20 cursor-pointer' onClick={() => onChangePreviousQuoteOrder(nextV || 0)} />
                    </div> 
                : null}
            </div>
            {openedModal ? <WhiteBox className='absolute w-310 mt-5 right-0 p-24 bg-white z-10 top-full' >
                <div>
                    <TwinTrans transKey='selectVersionToLoad'>Selecciona la versión a cargar</TwinTrans>
                </div>
                <div className='mt-20 overflow-auto max-h-200'>
                    {renderThis}
                </div>
                <div className='h-1 w-full bg-gray-EE'></div>
                <div className='mt-20'>
                    <ButtonPrimary className='w-full' onClick={() => onChangePreviousQuoteOrder(previousQuoteOrders.selectedQuoteVersion)}><TwinTrans transKey='loadVersion'>Cargar versión</TwinTrans></ButtonPrimary>
                </div>
            </WhiteBox> : null}
        </div>
    )
}

interface QuoteOrderButtonsPreviousData {
    previousQuoteOrders: QuoteOrderModelType[] | null
    selectedQuoteVersion: number
}

interface QuoteOrderVersionsSelectorLogicProps {
    url: string
    currentValue: number
    versions: QuoteOrderModelType[]
}

const useQuoteOrderVersionsSelectorLogic = ({ url, currentValue, versions }: QuoteOrderVersionsSelectorLogicProps) => {
    const navigate = useNavigate()
    const [previousQuoteOrders, setPreviousQuoteOrders] = useState<QuoteOrderButtonsPreviousData>({ previousQuoteOrders: versions, selectedQuoteVersion: currentValue })
    const [openedModal, setOpenedModal] = useState<ModalOpenedTy>(null)
    const ref = useRef<any>(null)

    useOnClickOutside(ref, () => setOpenedModal(null))

    const changeOpened = useCallback(async () => {
        setOpenedModal((old) => {
            return old ? null : true
        })
    }, [setOpenedModal])

    const onChangeQuoteVersionChecked = useCallback((value: number) => {
        setPreviousQuoteOrders((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, selectedQuoteVersion: value }
        })
    }, [setPreviousQuoteOrders])

    const onChangePreviousQuoteOrder = useCallback((value: number) => {
        navigate(url + '/' + value)
        setPreviousQuoteOrders((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, selectedQuoteVersion: value }
        })
        setOpenedModal(null)
    }, [navigate, url])

    return { previousQuoteOrders, onChangePreviousQuoteOrder, onChangeQuoteVersionChecked, ref, openedModal, changeOpened }
}


interface QuoteOrderDuplicateButtonProps {
    allRowData: OrderData
    title: string
    children?: React.ReactNode
    id: string
}

export const QuoteOrderDuplicateButton: React.FC<QuoteOrderDuplicateButtonProps> = ({ allRowData, title, children, id }) => {
    const { openModal, setOpenModal } = useQuoteOrderDuplicateButtonLogic()
    return (
        <PermissionChecker permission='create' userPermissions={getUserPermissions('quote.order')}>
            <div onClick={() => setOpenModal({ type: 'create', allRowData })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-green-43 regular22' message={title} icon={faCopy} id={id} />}
            </div>
            {openModal?.type === 'create' ? <ModalQuoteOrderDuplicate setOpened={() => setOpenModal({ type: 'delete', allRowData })} allRowData={allRowData} onSubmit={() => { }} /> : null}
        </PermissionChecker>
    )
}

const useQuoteOrderDuplicateButtonLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()

    return {openModal, setOpenModal}
}

interface QuoteOrderReopenButtonProps extends QuoteOrderReopenButtonLogicProps {
    onSubmit?: () => void
    id: string
    title: string
    OrderId: number
    children?: React.ReactNode
}

export const QuoteOrderReopenButton: React.FC<QuoteOrderReopenButtonProps> = ({ OrderId, onSubmit, id, title, children }) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const { onReopenQuoteOrder } = useQuoteOrderReopenButtonLogic({ onSubmit })
    return (
        <PermissionChecker permission='update' userPermissions={getUserPermissions('quote.order')}>
            <div onClick={() => setOpenModal({ allRowData: { id: OrderId }, type: 'edit' })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-green-43 regular22' message={title} icon={faArrowRotateRight} id={id} />}
            </div>
            {openModal?.type === 'edit' ? <ModalReopenQuote setOpened={() => setOpenModal(null)} onSubmit={onReopenQuoteOrder} allRowData={openModal.allRowData} /> : null}
        </PermissionChecker>
    )
}

interface QuoteOrderReopenButtonLogicProps {
    onSubmit?: () => void
}

const useQuoteOrderReopenButtonLogic = ({ onSubmit }: QuoteOrderReopenButtonLogicProps) => {
    const location = useLocation()
    const path = getBasePath(location.pathname, 'single')
    const navigate = useNavigate()
    const onReopenQuoteOrder = useCallback(async (responseJSON: any) => {
        if (responseJSON?.id) {
            await navigate(path + '/' + responseJSON.id)
            onSubmit?.()
        }
    }, [onSubmit, navigate, path])

    return { onReopenQuoteOrder }
}

interface QuoteOrderShareButtonProps {
    allRowData: OrderData
    onSubmit: () => void
    OrderId: number
    id: string
    title: string
    children?: React.ReactNode
}

export const QuoteOrderShareButton: React.FC<QuoteOrderShareButtonProps> = ({ allRowData, OrderId, onSubmit, id, title, children }) => {
    const publicToken = allRowData.header.publicToken
    const { openModal, setOpenModal } = useEditDeleteModal()

    return (
        <PermissionChecker permission='update' userPermissions={getUserPermissions('quote.order')}>
            <PermissionChecker permission='read' userPermissions={getUserPermissions('customer.profile.contactPerson')}>
                <div onClick={() => setOpenModal({ allRowData: { id: OrderId, share: !publicToken, CustomerId: allRowData.header.CustomerId }, type: 'edit' })}>
                    {children ?? <TwinIconToolTip classNameIcon={'regular22 hover:text-green-43 ' + (publicToken ? 'hover:text-green-43' : '')} message={title} icon={publicToken ? faShareNodes : faShare} id={id} />}
                </div>
                {openModal?.type === 'edit' ? <ModalShareAndUnshareQuote setOpened={() => setOpenModal(null)} onSubmit={() => onSubmit?.()} allRowData={allRowData} /> : null}
            </PermissionChecker>
        </PermissionChecker>
    )
}

interface QuoteOrderCommentsButtonProps {
    allRowData: OrderData
    onSubmit: () => void
    title: string
    id: string
    readOnly?: boolean
    children?: React.ReactNode
}

export const QuoteOrderCommentsButton: React.FC<QuoteOrderCommentsButtonProps> = ({ allRowData, onSubmit, title, id, readOnly, children }) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const allMessagesReaded = !allRowData?.header?.OrderComment?.OrderCommentMessages?.length || allRowData?.header?.OrderComment?.OrderCommentMessages?.[0]?.readed

    return (
        <Fragment>
            <div onClick={() => setOpenModal({ type: 'create', allRowData })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-green-43 regular22' message={title} icon={faComments} id={id} >
                    <div className={(allMessagesReaded ? 'hidden' : '') + ' rounded-full bg-green-43 w-8 h-8 absolute z-3 -top-1 right-0'}></div>
                </TwinIconToolTip>}
            </div>
            {openModal?.type === 'create' ? <ModalQuoteComments setOpened={() => setOpenModal({ type: 'delete', allRowData })} allRowData={allRowData} onSubmit={onSubmit} readOnly={readOnly} /> : null}
        </Fragment>
    )
}

interface NewQuoteOrderButtonProps {
    id: string
    title: string
    disabled: boolean
    infoMessage: string
    url: string
    OrderId: number
    onSubmit: () => void
    className?: string
    children?: React.ReactNode
}

export const NewQuoteOrderButton: React.FC<NewQuoteOrderButtonProps> = ({ id, title, disabled, className, infoMessage, children, ...rest }) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    return (
        <Fragment>
            <div onClick={() => !disabled ? setOpenModal({ allRowData: { id: rest.OrderId }, type: 'edit' }) : undefined}>
                {children ?? <TwinIconToolTip classNameIcon={'regular22 ' + (className || '') + (disabled ? ' text-red-BA' : ' hover:text-green-43')} message={disabled ? infoMessage : title} icon={faFilePlus} id={id} />}
            </div>
            {openModal?.type === 'edit' ? <ModalNewVersionQuote setOpened={() => setOpenModal(null)} {...rest}/> : null }
        </Fragment>
    )
}


export interface QuoteOrderButtonProps {
    id: string
    url: string
    title: string
    OrderId: number
    orderStatus: number
    disabled: boolean
    infoMessage: string
    className?: string
    onSubmit?: () => void
    children?: React.ReactNode
}
export const QuoteOrderButton: React.FC<QuoteOrderButtonProps> = ({ id, title, disabled, className, infoMessage, url, OrderId, orderStatus, onSubmit, children }) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const message = disabled ? infoMessage : title
    return (
        <Fragment>
            <div onClick={() => !disabled ? setOpenModal({ allRowData: { id: OrderId, status: orderStatus }, type: 'edit' }) : undefined}>
                {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 ' + (className || '') + (disabled ? ' text-red-BA' : ' text-green-43')} message={message} icon={faCheckCircle} />}
            </div>
            {openModal?.type === 'edit' ? <ModalFinalizeQuote url={url} setOpened={() => setOpenModal(null)} onSubmit={() => onSubmit?.()} allRowData={openModal.allRowData} /> : null}
        </Fragment>
    )
}

export const QuoteOrderButtonCancel: React.FC<QuoteOrderButtonProps> = ({ id, title, url, disabled, className, infoMessage, OrderId, orderStatus, onSubmit, children }) => {
    const {openModal, setOpenModal} = useEditDeleteModal()
    return (
        <Fragment>
            <div onClick={() => !disabled ? setOpenModal({ allRowData: { id: OrderId, status: orderStatus }, type: 'delete' }) : undefined}>
                {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 text-red-BA ' + (className || '')} message={disabled ? infoMessage : title} icon={faXmarkCircle} />}
            </div>
            {openModal?.type === 'delete' ? <ModalCancelQuote url={url || ''} setOpened={() => setOpenModal(null)} onSubmit={() => onSubmit?.()} allRowData={openModal.allRowData}/> : null}
        </Fragment>
    )
}
interface QuoteOrderDownloadButtonPDFProps extends QuoteOrderDownloadButtonPDFLogicProps {
    id: string
    title: string
    className?: string
    children?: React.ReactNode
}
export const QuoteOrderDownloadButtonPDF: React.FC<QuoteOrderDownloadButtonPDFProps> = ({ id, title, className, children, ...logic }) => {
    const { downloadPdf, loading } = useQuoteOrderDownloadButtonPDFLogic({ ...logic })

    if (loading) {
        return <LoadingSpinner className='h-22' />
    }

    return (
        <div onClick={downloadPdf} >
            {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faFilePdf} />}
            <a className='hidden downloadfile' download={true} href='/' target='_blank' onClick={(e) => e.stopPropagation()}>a</a>
        </div>
    )
}

interface QuoteOrderDownloadButtonPDFLogicProps {
    Order: TwinDictionary
}

const useQuoteOrderDownloadButtonPDFLogic = ({ Order }: QuoteOrderDownloadButtonPDFLogicProps) => {
    const { t } = useTwinTranslation()
    const { endLoading, loading, startLoading } = useIsLoading()
    const errorNotPDFTemplate = t('notPDFTemplate', 'No tienes plantillas de PDF')

    const downloadPdf = useCallback(async (e: React.MouseEvent) => {
        startLoading()
        const current = e.currentTarget
        const result = await twinFetchPost('/api/app/order/quote/getQuoteOrderPdf', { id: Order.id })
        if (result) {
            if (result.status === 422) {
                const val = await result.json()
                if (val?.errors[0] && val?.errors[0].msg === 'Not have PDF Template') {
                    changeErrorMessage(errorNotPDFTemplate)
                }
                endLoading()
                return false
            }
            const pdf = await result.blob()
            const fileURL = URL.createObjectURL(pdf)
            const a = current.querySelector('a')
            const fileName = `${Order.full_assigned_number}-${Order?.Customer?.fiscal_name || Order?.Customer?.name}`
            if (a) {
                a.href = fileURL
                a.download = fileName + '.pdf'
                a.click()
            }
            endLoading()
        }
    }, [Order, errorNotPDFTemplate, endLoading, startLoading])

    return { downloadPdf, loading }
}

interface QuoteOrderDownloadPdfTemplateButtonProps {
    id: string
    Order: TwinDictionary
    title: string
    className?: string
    children?: React.ReactNode
}

export const QuoteOrderDownloadPdfTemplateButton: React.FC<QuoteOrderDownloadPdfTemplateButtonProps> = ({ id, title, className, Order, children }) => {
    const { showTemplateSelector, setShowTemplateSelector, startLoading, endLoading, loading, ref } = useQuoteOrderDownloadPdfTemplateButtonLogic()
    return (
        <Fragment>
            <div className={'flex flex-col relative ' + (className || '')} ref={ref} onClick={() => setShowTemplateSelector((old) => !old)}>
                {loading
                    ? <div><LoadingSpinner className='w-24 h-24 m-auto' /></div>
                    : 
                    <Fragment>
                        {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faCloudArrowDown} />}
                    </Fragment>
                }
                {showTemplateSelector ? <QuoteOrderSelectorPdfTemplate Order={Order} handleShowTemplateSelector={() => setShowTemplateSelector((old) => !old)} startLoading={startLoading} endLoading={endLoading} /> : null}
            </div>
        </Fragment>
    )
}


const useQuoteOrderDownloadPdfTemplateButtonLogic = () => {
    const ref = useRef<any>(null)
    const [showTemplateSelector, setShowTemplateSelector] = useState<boolean>(false)
    const { startLoading, endLoading, loading } = useIsLoading()
    useOnClickOutside(ref, () => setShowTemplateSelector(false))

    return { showTemplateSelector, setShowTemplateSelector, startLoading, endLoading, loading, ref }
}

interface QuoteSelectorPdfTemplateProps extends QuoteOrderSelectorPdfTemplateLogicProps {}

const QuoteOrderSelectorPdfTemplate: React.FC<QuoteSelectorPdfTemplateProps> = ({ ...rest }) => {
    const { ref, downloadPdfWithTemplate } = useQuoteOrderSelectorPdfTemplateLogic({ ...rest })
    return (
        <OrderSelectorPdfTemplateConnect downloadPdfWithTemplate={downloadPdfWithTemplate} myref={ref} typeTemplate={'quoteOrder'} />
    )
}

interface QuoteOrderSelectorPdfTemplateLogicProps {
    Order: TwinDictionary
    handleShowTemplateSelector: () => void
    startLoading: () => true
    endLoading: () => void
}

const useQuoteOrderSelectorPdfTemplateLogic = ({ Order, handleShowTemplateSelector, startLoading, endLoading }: QuoteOrderSelectorPdfTemplateLogicProps) => {
    const ref = useRef<any>(null)
    const downloadPdfWithTemplate = useCallback(async (PdfTemplateId: number | string) => {
        startLoading()
        handleShowTemplateSelector()
        const current = ref.current
        const result = await twinFetchPost('/api/app/order/quote/getQuoteOrderPdf', { id: Order.id, PdfTemplateId })
        if (result) {
            const pdf = await result.blob()
            const fileURL = URL.createObjectURL(pdf)
            const a = current.querySelector('a')
            const fileName =  `${Order?.full_assigned_number}-${Order?.Customer?.fiscal_name || Order?.Customer?.name}`
            if (a) {
                a.href = fileURL
                a.download = fileName + '.pdf'
                a.click()
            }
            endLoading()
        }
    }, [Order, handleShowTemplateSelector, endLoading, startLoading])

    return { ref, downloadPdfWithTemplate }
}

interface QuoteOrderRecalculateButtonProps {
    allRowData: OrderData
    onSubmit?: () => void
    children?: React.ReactNode
    title: string
    id: string
}

export const QuoteOrderRecalculateButton: React.FC<QuoteOrderRecalculateButtonProps> = ({ allRowData, onSubmit, children, id, title }) => {
    const { openModal, setOpenModal } = useQuoteOrderRecalculateButtonLogic()
    return (
        <PermissionChecker permission='update' userPermissions={getUserPermissions('quote.order')}>
            <div onClick={() => setOpenModal({ type: 'create', allRowData })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-green-43 regular22' message={title} icon={faArrowsRotate} id={id} />}
            </div>
            {openModal?.type === 'create' ? <ModalRecalculateQuote setOpened={() => setOpenModal({ type: 'delete', allRowData })} allRowData={allRowData} onSubmit={() => onSubmit?.()} /> : null}
        </PermissionChecker>
    )
}

const useQuoteOrderRecalculateButtonLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()

    return { openModal, setOpenModal }
}